/** Binds one ranked pop-out row to the group's move callback. */
import React, { memo, useCallback, useMemo } from "react";

import type { WatchlistMoveDestination } from "@/features/scouting/components/molecules/WatchlistOrderControls/WatchlistOrderControlsContainer";

import WatchlistPositionPopoutRowView, {
  type PositionPopoutRow,
  type PositionPopoutRowAction,
} from "./WatchlistPositionPopoutRowView";

/** Public props for one pop-out row. */
export interface WatchlistPositionPopoutRowProps {
  readonly row: PositionPopoutRow;
  readonly canEdit: boolean;
  readonly paused: boolean;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onMove: (
    playerId: string,
    destination: WatchlistMoveDestination
  ) => void;
  /** PV22: the + per row, when the opening offers one. */
  readonly rowAction?: PositionPopoutRowAction;
}

/**
 * Curries the row's player identity into the group move handler.
 * @param props - Row data and group callbacks.
 * @param props.row - Ranked member rendered by this row.
 * @param props.canEdit - Whether reorder controls are enabled.
 * @param props.paused - Whether a search pauses reordering.
 * @param props.isFirst - Whether the row is first in the group.
 * @param props.isLast - Whether the row is last in the group.
 * @param props.onMove - Moves one player relative to the group.
 * @param props.rowAction - PV22: the + per row, when offered.
 */
const WatchlistPositionPopoutRowContainer = ({
  row,
  canEdit,
  paused,
  isFirst,
  isLast,
  onMove,
  rowAction,
}: WatchlistPositionPopoutRowProps) => {
  const handleMove = useCallback(
    (destination: WatchlistMoveDestination) =>
      onMove(row.playerId, destination),
    [onMove, row.playerId]
  );
  const onRowAction = rowAction?.onPress;
  const handleRowAction = useCallback(
    () => onRowAction?.(row.playerId),
    [onRowAction, row.playerId]
  );
  const rowPending = rowAction?.pending[row.playerId];
  const boundAction = useMemo(
    () =>
      rowAction
        ? {
            label: rowAction.label,
            pending: rowPending,
            onPress: handleRowAction,
          }
        : undefined,
    [rowAction, rowPending, handleRowAction]
  );
  return (
    <WatchlistPositionPopoutRowView
      row={row}
      canEdit={canEdit}
      paused={paused}
      isFirst={isFirst}
      isLast={isLast}
      onMove={handleMove}
      rowAction={boundAction}
    />
  );
};

WatchlistPositionPopoutRowContainer.displayName =
  "WatchlistPositionPopoutRowContainer";
export default memo(WatchlistPositionPopoutRowContainer);
